import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  ClipboardList, Calendar, Archive, Settings, Inbox, CalendarDays, Users,
  LayoutDashboard, Building2, BarChart3, X, Activity, Sun, Moon, LogOut,
  HeartPulse, FileText, Home, BookOpen, Thermometer, MessageCircle,
  AlertTriangle, Video, BriefcaseMedical, FileClock, Wallet,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useTheme } from '@/hooks/useTheme';

const roleMenus = {
  mmg: {
    title: 'MMG',
    shortTitle: 'MMG',
    items: [
      { label: 'Dashboard Operativa', icon: LayoutDashboard, path: '/mmg' },
      { label: 'Pazienti in Carico', icon: Users, path: '/mmg/pazienti' },
      { label: 'Programmato MMG', icon: CalendarDays, path: '/mmg/programmato' },
      { label: 'Le Mie Richieste', icon: FileText, path: '/mmg/richieste' },
      { label: 'Prescrizioni Rapide', icon: Activity, path: '/mmg/prescrizioni' },
      { label: 'Comunicazioni', icon: Inbox, path: '/mmg/comunicazioni' },
    ],
  },
  segreteria: {
    title: 'Centrale Operativa Territoriale',
    shortTitle: 'COT',
    items: [
      { label: 'Dashboard Centrale', icon: LayoutDashboard, path: '/segreteria' },
      { label: 'Triage & Richieste', icon: Inbox, path: '/segreteria/triage' },
      { label: 'Centrale Allarmi Clinici', icon: AlertTriangle, path: '/segreteria/allarmi' },
      { label: 'Dimissioni Protette', icon: BriefcaseMedical, path: '/segreteria/dimissioni' },
      { label: 'Hub Teleconsulti', icon: Video, path: '/segreteria/teleconsulti' },
    ],
  },
  medico: {
    title: 'Medico Valutatore UVMD',
    shortTitle: 'Medico UVMD',
    items: [
      { label: 'Agenda', icon: CalendarDays, path: '/medico' },
      { label: 'Bozze in corso', icon: FileClock, path: '/medico/bozze' },
      { label: 'Pazienti', icon: Users, path: '/medico/pazienti' },
    ],
  },
  'case-manager': {
    title: 'Case Manager',
    shortTitle: 'Case Manager',
    items: [
      { label: 'Cruscotto Operativo', icon: LayoutDashboard, path: '/case-manager' },
      { label: 'Cronologia Pazienti', icon: CalendarDays, path: '/case-manager/cronologia' },
      { label: 'PAI Builder', icon: HeartPulse, path: '/case-manager/pai-builder' },
      { label: 'Logistica Erogatori', icon: Building2, path: '/case-manager/logistica' },
      { label: 'Diario Clinico', icon: FileText, path: '/case-manager/diario' },
      { label: 'Comunicazioni', icon: Inbox, path: '/case-manager/comunicazioni' },
    ],
  },
  operatore: {
    title: 'Operatore Sanitario',
    shortTitle: 'Operatore',
    items: [
      { label: 'Agenda Giornaliera', icon: CalendarDays, path: '/operatore' },
      { label: 'Archivio Pazienti', icon: Archive, path: '/operatore/archivio' },
      { label: 'Comunicazioni', icon: Inbox, path: '/operatore/comunicazioni' },
    ],
  },
  paziente: {
    title: 'Portale Assistito',
    shortTitle: 'Paziente',
    items: [
      { label: 'Home', icon: Home, path: '/paziente' },
      { label: 'Il mio PAI', icon: HeartPulse, path: '/paziente/pai' },
      { label: 'Agenda', icon: CalendarDays, path: '/paziente/agenda' },
      { label: 'Telemonitoraggio', icon: Thermometer, path: '/paziente/telemonitoraggio' },
      { label: 'Comunicazioni & SOS', icon: MessageCircle, path: '/paziente/comunicazioni' },
      { label: 'Archivio & Educazione', icon: BookOpen, path: '/paziente/archivio' },
      { label: 'Privacy e Consensi', icon: Settings, path: '/paziente/privacy' },
    ],
  },
  asl: {
    title: 'Direzione ASL',
    shortTitle: 'ASL',
    items: [
      { label: 'Control Tower', icon: LayoutDashboard, path: '/asl' },
      { label: 'Economico-Finanziario', icon: Activity, path: '/asl/finance' },
      { label: 'Budget di Salute', icon: Wallet, path: '/asl/budget-salute' },
      { label: 'Performance Erogatori', icon: BarChart3, path: '/asl/performance' },
      { label: 'Valore ed Esiti', icon: HeartPulse, path: '/asl/valore' },
      { label: 'Simulazione What-If', icon: Settings, path: '/asl/simulazione' },
    ],
  },
  pua: {
    title: 'Punto Unico di Accesso',
    shortTitle: 'PUA',
    items: [
      { label: 'Le mie Segnalazioni', icon: LayoutDashboard, path: '/pua' },
      { label: 'Nuova Richiesta ADI', icon: ClipboardList, path: '/pua/nuova-richiesta' },
    ],
  },
  ospedale: {
    title: 'Medico Ospedaliero',
    shortTitle: 'Ospedale',
    items: [
      { label: 'Control Room', icon: LayoutDashboard, path: '/ospedale' },
      { label: 'Nuova Richiesta ADI', icon: ClipboardList, path: '/ospedale/richiesta' },
      { label: 'Dimissione Concordata', icon: BriefcaseMedical, path: '/ospedale/concordata' },
      { label: 'Follow-up dimissioni', icon: HeartPulse, path: '/ospedale/followup' },
    ],
  },
};

export { roleMenus };

function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  return (
    <div
      data-testid="theme-toggle-btn"
      role="button"
      tabIndex={0}
      onClick={toggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(); }}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-200 cursor-pointer select-none"
    >
      {isDark ? <Moon className="w-[18px] h-[18px]" /> : <Sun className="w-[18px] h-[18px]" />}
      <span>Tema Scuro</span>
      <Switch
        checked={isDark}
        onCheckedChange={toggle}
        className="ml-auto"
        data-testid="theme-toggle-switch"
      />
    </div>
  );
}

function LogoutButton({ onLogout }) {
  const navigate = useNavigate();
  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate('/');
  };
  return (
    <button
      data-testid="sidebar-logout-btn"
      onClick={handleLogout}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
    >
      <LogOut className="w-[18px] h-[18px]" />
      <span>Esci</span>
    </button>
  );
}

export function Sidebar({ role, isOpen, onToggle, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const menu = roleMenus[role];

  if (!menu) return null;

  const isActive = (path) => {
    if (path === `/${role}` || path === '/segreteria') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={onToggle}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={cn(
          "fixed top-0 left-0 z-50 h-screen flex flex-col border-r border-border bg-card",
          "transition-transform duration-300 ease-in-out",
          "w-[260px]",
          isOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0 lg:static lg:z-auto"
        )}
      >
        {/* Logo Header */}
        <div className="flex items-center justify-between px-5 h-16 border-b border-border shrink-0">
          <div className="flex items-center gap-2.5">
            <img
              src="/witapp-logo.svg"
              alt="WitApp"
              className="h-8 w-auto"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <div className="hidden items-center justify-center w-8 h-8 rounded-lg bg-gradient-comfort">
              <Activity className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-foreground leading-tight tracking-tight">ADI Platform</h1>
              <p className="text-[10px] text-muted-foreground leading-tight">Assistenza Domiciliare</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-muted-foreground h-8 w-8"
            onClick={onToggle}
            data-testid="sidebar-close-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Role Badge */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/8 border border-primary/12">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-medium text-foreground">{menu.shortTitle}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 mb-2">
            Navigazione
          </p>
          {menu.items.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                data-testid={`nav-item-${item.path.replace(/\//g, '-').slice(1)}`}
                onClick={() => { navigate(item.path); if (window.innerWidth < 1024) onToggle(); }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium",
                  "transition-all duration-200",
                  active
                    ? "bg-primary/10 text-primary shadow-sm"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className={cn("w-[18px] h-[18px] flex-shrink-0", active && "text-primary")} />
                <span>{item.label}</span>
                {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />}
              </button>
            );
          })}
        </nav>

        {/* Theme Toggle + Logout + Settings at bottom */}
        <div className="border-t border-border px-3 py-3 shrink-0 space-y-1">
          <ThemeToggle />
          <button
            data-testid="sidebar-settings-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-200"
          >
            <Settings className="w-[18px] h-[18px]" />
            <span>Impostazioni</span>
          </button>
          <LogoutButton onLogout={onLogout} />
        </div>
      </aside>
    </>
  );
}
